import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Typography from '@material-ui/core/Typography';
import { ENDPOINTS, OTHER_OUTCOME_TASK, TASK_STATUS } from 'features/daily-plan/constants';
import strings from 'strings';
import TextBox from 'UI/components/atoms/TextBox';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import FPToggleButton from 'UI/components/molecules/FPToggleButton';
import { idOptionSelected, REQUIRED_VALIDATION } from 'UI/utils';
import { buildBasePropsForField } from 'UI/utils/forms';
import { Groupers } from 'UI/utils/renderers';

import { CALL_TYPES_RADIO_OPTIONS, FORM_FIELDS_MAP } from './TaskResults.constants';
import { FieldContainer, FormContainer, OtherOutcomeContainer } from './TaskResults.styled';

const { form: COPIES } = strings.dailyPlan.drawer.taskDetails;

const TASK_STATUSES = [
  {
    title: TASK_STATUS.completed.title,
    value: TASK_STATUS.completed.id
  },
  {
    title: TASK_STATUS.pending.title,
    value: TASK_STATUS.pending.id
  }
];

export const TaskResults = ({ taskStatusId, taskTypeId }) => {
  const { errors, register, unregister, watch, setValue } = useFormContext();

  const formValues = watch();

  const isOtherOutcome = formValues[FORM_FIELDS_MAP.Outcome.key]?.id === OTHER_OUTCOME_TASK.id;

  useEffect(() => {
    register(FORM_FIELDS_MAP.CallType.key);
    register(FORM_FIELDS_MAP.Status.key);
    register(FORM_FIELDS_MAP.Outcome.key);

    return () => {
      unregister(FORM_FIELDS_MAP.CallType.key);
      unregister(FORM_FIELDS_MAP.Status.key);
      unregister(FORM_FIELDS_MAP.Outcome.key);
    };
  }, [register, unregister]);

  const handleRadioChange = event => {
    const { value } = event.target;
    setValue(FORM_FIELDS_MAP.CallType.key, value, true);
  };

  const handleToggleChange = value => {
    setValue(FORM_FIELDS_MAP.Status.key, value, true);
  };

  const handleSelectChange = (name, value) => {
    setValue(name, Array.isArray(value) && !value.length ? null : value, true);
  };

  const handleTextBoxChange = (name, value) => {
    setValue(name, value);
  };

  return (
    <>
      <Typography variant="subtitle1">{COPIES.titles.taskResults}</Typography>

      <FormContainer container direction="column" spacing={3}>
        <FieldContainer container item>
          <Grid item xs={2}>
            <Typography variant="body1">
              <b>{COPIES.labels.connectionResult}</b>
            </Typography>
          </Grid>
          <Grid item xs={10}>
            <FormControl component="fieldset" error={!!errors[FORM_FIELDS_MAP.CallType.key]}>
              <RadioGroup
                aria-label="callType"
                name={FORM_FIELDS_MAP.CallType.key}
                value={formValues[FORM_FIELDS_MAP.CallType.key] || null}
                onChange={handleRadioChange}
                row
              >
                {CALL_TYPES_RADIO_OPTIONS.map(item => (
                  <FormControlLabel
                    key={item.id}
                    value={String(item.id)}
                    control={<Radio color="primary" />}
                    label={item.label}
                    labelPlacement="end"
                  />
                ))}
              </RadioGroup>
              <FormHelperText>{errors[FORM_FIELDS_MAP.CallType.key]?.message}</FormHelperText>
            </FormControl>
          </Grid>
        </FieldContainer>

        <FieldContainer container item>
          <Grid item xs={2}>
            <Typography variant="body1">
              <b>{COPIES.labels.status}</b>
            </Typography>
          </Grid>

          <Grid item xs={10}>
            <FormControl component="fieldset" error={!!errors[FORM_FIELDS_MAP.Status.key]}>
              <FPToggleButton
                onChange={handleToggleChange}
                options={TASK_STATUSES}
                initialValue={taskStatusId || null}
                size="small"
              />
              <FormHelperText>{errors[FORM_FIELDS_MAP.Status.key]?.message}</FormHelperText>
            </FormControl>
          </Grid>
        </FieldContainer>

        <FieldContainer container item>
          <Grid item xs={2}>
            <Typography variant="body1">
              <b>{COPIES.labels.outcome}</b>
            </Typography>
          </Grid>

          <Grid item xs={10}>
            <AutocompleteSelect
              error={!!errors[FORM_FIELDS_MAP.Outcome.key]}
              errorText={errors[FORM_FIELDS_MAP.Outcome.key]?.message}
              getOptionLabel={option => option?.title ?? null}
              getOptionSelected={idOptionSelected}
              groupBy={Groupers.byCategory}
              name={FORM_FIELDS_MAP.Outcome.key}
              onSelect={handleSelectChange}
              label={COPIES.labels.selectTaskOutcome}
              selectedValue={formValues[FORM_FIELDS_MAP.Outcome.key]}
              url={`${ENDPOINTS.outcomes}?taskTypeId=${taskTypeId}`}
            />
          </Grid>
        </FieldContainer>

        {isOtherOutcome && (
          <FieldContainer container item>
            <OtherOutcomeContainer item xs={10}>
              <TextBox
                {...buildBasePropsForField(FORM_FIELDS_MAP.CustomOutcome.key, errors)}
                inputRef={register(REQUIRED_VALIDATION)}
                label={COPIES.labels.customOutcome}
              />
            </OtherOutcomeContainer>
          </FieldContainer>
        )}
      </FormContainer>

      <Grid container direction="column">
        <Typography variant="body1">{COPIES.labels.comments}</Typography>

        <TextBox
          inputRef={register()}
          multiline
          name={FORM_FIELDS_MAP.Comments.key}
          onChange={handleTextBoxChange}
          value={formValues[FORM_FIELDS_MAP.Comments.key]}
        />
      </Grid>
    </>
  );
};
